/**
 * InspectorPanel: shows the selected object's editable properties (label,
 * seats, lock state) plus rotate/duplicate/delete actions. On desktop this
 * sits as a fixed right-side panel; on tablet/touch it should render as a
 * bottom sheet instead (handled by the parent EditorPage's responsive
 * layout, not by this component itself — this component is layout-agnostic
 * and just fills whatever container it's given).
 *
 * ROW GROUPING (chair rows):
 * When a single standalone_chair that has a rowGroupId is selected, the panel
 * shows a "Select Row" button. Clicking it selects every chair in the same row,
 * which the coordinator can then move or delete together via the multi-select
 * panel. Every chair remains a real independent object — the rowGroupId is
 * purely a UI-level grouping hint, never a structural constraint.
 */

import { useEditorStore } from '../../store/useEditorStore';
import type { ObjectCatalogEntry } from '../../domain/types';

interface InspectorPanelProps {
  catalog: ObjectCatalogEntry[];
}

export function InspectorPanel({ catalog }: InspectorPanelProps) {
  const selectedIds = useEditorStore((state) => state.selectedIds);
  const objects = useEditorStore((state) => state.objects);
  const updateObject = useEditorStore((state) => state.updateObject);
  const deleteObject = useEditorStore((state) => state.deleteObject);
  const deleteSelected = useEditorStore((state) => state.deleteSelected);
  const duplicateObject = useEditorStore((state) => state.duplicateObject);
  const duplicateSelected = useEditorStore((state) => state.duplicateSelected);
  const toggleLock = useEditorStore((state) => state.toggleLock);
  const toggleLockSelected = useEditorStore((state) => state.toggleLockSelected);
  const selectRowGroup = useEditorStore((state) => state.selectRowGroup);

  if (selectedIds.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center p-6 text-center">
        <p className="font-body text-sm text-ink-400">
          Select an object on the canvas to edit it.
        </p>
        <p className="mt-1 font-body text-xs text-ink-400">
          Shift-click to select more than one at a time.
        </p>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // MULTI-SELECT panel
  // -------------------------------------------------------------------------
  if (selectedIds.length > 1) {
    const selectedObjects = objects.filter((o) => selectedIds.includes(o.id));
    const allLocked = selectedObjects.every((o) => o.locked);

    // Detect whether all selected objects share the same rowGroupId —
    // if so we know this is a coherent chair row and can offer "Delete Row"
    // with clearer labeling than the generic "Delete all selected".
    const firstRowGroup = selectedObjects[0]?.rowGroupId;
    const isCoherentRow =
      !!firstRowGroup &&
      selectedObjects.every((o) => o.rowGroupId === firstRowGroup);

    return (
      <div className="flex h-full flex-col p-4">
        <p className="font-body text-sm font-medium text-ink-700">
          {isCoherentRow
            ? `Row of ${selectedIds.length} chairs selected`
            : `${selectedIds.length} objects selected`}
        </p>
        <p className="mt-1 font-body text-xs text-ink-400">
          Drag any one to move the whole group together.
        </p>

        <div className="mt-4 grid grid-cols-2 gap-2">
          <button
            onClick={duplicateSelected}
            className="rounded border border-evergreen-300 px-3 py-2 font-body text-sm text-evergreen-700 hover:bg-evergreen-50"
          >
            Duplicate all
          </button>
          <button
            onClick={toggleLockSelected}
            className="rounded border border-evergreen-300 px-3 py-2 font-body text-sm text-evergreen-700 hover:bg-evergreen-50"
          >
            {allLocked ? 'Unlock all' : 'Lock all'}
          </button>
        </div>

        <button
          onClick={deleteSelected}
          className="mt-2 rounded border border-danger px-3 py-2 font-body text-sm text-danger hover:bg-danger/5"
        >
          {isCoherentRow ? 'Delete row' : 'Delete all selected'}
        </button>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // SINGLE-SELECT panel
  // -------------------------------------------------------------------------
  const object = objects.find((o) => o.id === selectedIds[0]);
  if (!object) return null;

  const catalogEntry = catalog.find((c) => c.itemType === object.type);
  const maxSeats = catalogEntry?.maxSeats ?? null;

  // Is this a chair that belongs to a row group? If so, how many chairs are in its row?
  const isChairInRow = object.type === 'standalone_chair' && !!object.rowGroupId;
  const rowMemberCount = isChairInRow
    ? objects.filter((o) => o.rowGroupId === object.rowGroupId).length
    : 0;

  return (
    <div className="flex h-full flex-col gap-5 p-4">
      <div>
        <p className="font-body text-xs uppercase tracking-wide text-ink-400">
          {catalogEntry?.displayName ?? object.type}
        </p>
        <h3 className="font-display text-lg text-ink-700">{object.label}</h3>
      </div>

      {/* ROW GROUP ACTION — shown only for chairs that belong to a row */}
      {isChairInRow && (
        <div className="rounded-lg border border-gold-200 bg-gold-50 px-3 py-3">
          <p className="font-body text-xs font-medium text-ink-600">
            Part of a row of {rowMemberCount} chairs
          </p>
          <p className="mt-0.5 font-body text-xs text-ink-400">
            Select the row to move or delete all chairs together.
          </p>
          <button
            onClick={() => selectRowGroup(object.id)}
            className="mt-2 w-full rounded border border-gold-400 bg-white px-3 py-2 font-body text-sm font-medium text-ink-700 hover:bg-gold-100"
          >
            Select Row ({rowMemberCount} chairs)
          </button>
        </div>
      )}

      <label className="block">
        <span className="font-body text-sm font-medium text-ink-600">Label</span>
        <input
          type="text"
          value={object.label}
          disabled={object.locked}
          onChange={(e) => updateObject(object.id, { label: e.target.value })}
          className="mt-1 w-full rounded border border-ivory-300 bg-white px-3 py-2 font-body text-sm text-ink-700 outline-none focus:border-gold-400 focus:ring-2 focus:ring-gold-200 disabled:bg-ivory-100 disabled:text-ink-400"
        />
      </label>

      {object.seats !== null && (
        <label className="block">
          <span className="font-body text-sm font-medium text-ink-600">
            Seats {maxSeats ? `(max ${maxSeats})` : ''}
          </span>
          <input
            type="number"
            min={0}
            max={maxSeats ?? undefined}
            value={object.seats}
            disabled={object.locked}
            onChange={(e) => updateObject(object.id, { seats: Number(e.target.value) })}
            className="mt-1 w-full rounded border border-ivory-300 bg-white px-3 py-2 font-body text-sm text-ink-700 outline-none focus:border-gold-400 focus:ring-2 focus:ring-gold-200 disabled:bg-ivory-100 disabled:text-ink-400"
          />
        </label>
      )}

      <div className="grid grid-cols-2 gap-2">
        <button
          onClick={() => duplicateObject(object.id)}
          className="rounded border border-evergreen-300 px-3 py-2 font-body text-sm text-evergreen-700 hover:bg-evergreen-50"
        >
          Duplicate
        </button>
        <button
          onClick={() => toggleLock(object.id)}
          className="rounded border border-evergreen-300 px-3 py-2 font-body text-sm text-evergreen-700 hover:bg-evergreen-50"
        >
          {object.locked ? 'Unlock' : 'Lock'}
        </button>
      </div>

      <button
        onClick={() => deleteObject(object.id)}
        disabled={object.locked}
        className="rounded border border-danger px-3 py-2 font-body text-sm text-danger hover:bg-danger/5 disabled:cursor-not-allowed disabled:opacity-40"
      >
        Delete
      </button>

      {object.locked && (
        <p className="font-body text-xs text-ink-400">
          This object is locked. Unlock it to move, resize, or delete it.
        </p>
      )}
    </div>
  );
}
